import * as React from 'react'
import { Tooltip } from 'react-tooltip'
import type * as Api from 'src/api'
import { CourseChosenIcon } from 'src/assets/icons/customIcons/course-icons/CourseChosen'
import { CourseCompletedIcon } from 'src/assets/icons/customIcons/course-icons/CourseCompleted'
import { CourseFailedIcon } from 'src/assets/icons/customIcons/course-icons/CourseFailed'
import { XlsIcon } from 'src/assets/icons/customIcons/Xls'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import * as Table from 'src/tailwind/components/Table'
import Loader from 'src/views/components/Loader'

interface Props {
  readonly groups: readonly Api.AdministratorCourseScoresGroup[]
  /**
   * The scores overview carries neither the quota nor lecturer avatars, so both come from the
   * course's groups list — the same data the lecturer page shows in its group rows.
   */
  readonly groupDetails: ReadonlyMap<number, Api.AdministratorCourseGroupItem>
  readonly isPending: boolean
  readonly selectedGroupId: number | null
  readonly onSelectGroup: (groupId: number) => void
  /**
   * While the criteria form is open the checkbox column picks the *other* groups the criteria are
   * copied onto — the same gesture the lecturer page uses to create several papers at once.
   * Otherwise it picks the groups a message goes to, and any group can be picked.
   */
  readonly isPickingGroups: boolean
  readonly pickedGroupIds: readonly number[]
  readonly onTogglePickedGroup: (groupId: number) => void
  readonly onPickAllGroups: (checked: boolean) => void
  readonly onSendMessage: () => void
  readonly onExport: () => void
  readonly onPrint: () => void
  readonly isExporting: boolean
}

export default function GroupsOverviewTable({
  groups,
  groupDetails,
  isPending,
  selectedGroupId,
  onSelectGroup,
  isPickingGroups,
  pickedGroupIds,
  onTogglePickedGroup,
  onPickAllGroups,
  onSendMessage,
  onExport,
  onPrint,
  isExporting,
}: Props): React.ReactElement {
  const t = useTranslatable()

  const pickableGroups = React.useMemo(
    () => (isPickingGroups ? groups.filter((group) => isPickable(group, selectedGroupId)) : groups),
    [groups, isPickingGroups, selectedGroupId]
  )

  const handleCheckAll = React.useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => onPickAllGroups(target.checked),
    [onPickAllGroups]
  )

  if (isPending) return <Loader className="m-auto flex" />

  return (
    <>
      <div className="flex w-full justify-end gap-2">
        <button
          type="button"
          onClick={onExport}
          disabled={isExporting}
          className="flex h-fit rounded-full p-2 text-primaryTextColor hover:bg-hover-icon disabled:opacity-50"
        >
          <div className="mr-2 h-fit w-[18px]">
            <XlsIcon width="18" height="18" />
          </div>
          {t('common:export')}
        </button>
        <button
          type="button"
          onClick={onPrint}
          disabled={isExporting}
          className="flex h-fit rounded-full p-2 text-primaryTextColor hover:bg-hover-icon disabled:opacity-50"
        >
          {t('common:print')}
        </button>
      </div>
      <div className="mb-[25px] mt-2 flex w-full flex-col items-end">
        <Table.Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th scope="col">
                <input
                  type="checkbox"
                  onChange={handleCheckAll}
                  checked={pickableGroups.length > 0 && pickedGroupIds.length === pickableGroups.length}
                  className="cursor-pointer border border-borderColor dark:bg-primaryWhite"
                />
              </Table.Th>
              <Table.Th scope="col" className="pl-0 text-start">
                {t('group:groups')}
              </Table.Th>
              <Table.Th scope="col" className="text-start">
                {t('lecturer:lecturers')}
              </Table.Th>
              <Table.Th align="center" scope="col" className="whitespace-nowrap !text-center">
                {t('common:status')}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map((group) => (
              <GroupRow
                key={group.id}
                group={group}
                details={groupDetails.get(group.id) ?? null}
                isSelected={selectedGroupId === group.id}
                isPickingGroups={isPickingGroups}
                isPicked={pickedGroupIds.includes(group.id)}
                onSelectGroup={onSelectGroup}
                onTogglePickedGroup={onTogglePickedGroup}
              />
            ))}
          </Table.Tbody>
        </Table.Table>
        {!isPickingGroups && (
          <Button
            variant="blue"
            className="mt-2"
            type="button"
            disabled={pickedGroupIds.length === 0}
            onClick={onSendMessage}
          >
            {t('message:send_message')}
          </Button>
        )}
      </div>
    </>
  )
}

/**
 * The edited group is always part of the save, and legacy refuses to copy criteria over a group
 * whose paper is active — a group with no paper, or a deactivated one, can still be picked.
 */
export function isPickable(group: Api.AdministratorCourseScoresGroup, selectedGroupId: number | null): boolean {
  return group.id !== selectedGroupId && group.paper?.isActive !== true
}

function GroupRow({
  group,
  details,
  isSelected,
  isPickingGroups,
  isPicked,
  onSelectGroup,
  onTogglePickedGroup,
}: {
  readonly group: Api.AdministratorCourseScoresGroup
  readonly details: Api.AdministratorCourseGroupItem | null
  readonly isSelected: boolean
  readonly isPickingGroups: boolean
  readonly isPicked: boolean
  readonly onSelectGroup: (groupId: number) => void
  readonly onTogglePickedGroup: (groupId: number) => void
}): React.ReactElement {
  const t = useTranslatable()
  const theme = useTheme()
  const tooltipId = `group-paper-status-${group.id}`
  const pickTooltipId = `group-pick-${group.id}`

  const handleSelect = React.useCallback(() => onSelectGroup(group.id), [group.id, onSelectGroup])
  const handleToggle = React.useCallback(() => onTogglePickedGroup(group.id), [group.id, onTogglePickedGroup])

  return (
    <Table.Tr className={isSelected ? 'bg-primaryGreen/50' : ''}>
      <Table.Td className="align-middle">
        {isPickingGroups ? (
          <>
            {!isSelected && group.paper?.isActive === true && (
              <Tooltip id={pickTooltipId} variant={theme === 'dark' ? 'dark' : 'light'}>
                {t('group:group_have_paper')}
              </Tooltip>
            )}
            <div data-tooltip-id={pickTooltipId}>
              <input
                type="checkbox"
                checked={isSelected || isPicked}
                // The edited group is implicit in the save and cannot be unpicked.
                disabled={isSelected || group.paper?.isActive === true}
                onChange={handleToggle}
                className="cursor-pointer border border-borderColor dark:bg-primaryWhite"
              />
            </div>
          </>
        ) : (
          <input
            type="checkbox"
            checked={isPicked}
            onChange={handleToggle}
            className="cursor-pointer border border-borderColor dark:bg-primaryWhite"
          />
        )}
      </Table.Td>
      <Table.Td className="items-start pl-0">
        <button type="button" className="pl-0 text-start text-primaryBlueLink no-underline" onClick={handleSelect}>
          {group.name}
          {details != null && `- (${details.quotaMin}-${details.quotaMax})`}
          <span>
            {' '}
            {t('common:total')} - {group.studentCount}
          </span>
        </button>
      </Table.Td>
      <Table.Td className="pl-0">
        <div className="flex flex-wrap items-start">
          {details?.lecturers != null
            ? details.lecturers.map((lecturer) => (
                <div key={lecturer.id} className="my-1 flex items-center">
                  {lecturer.photoUrl != null ? (
                    <img src={lecturer.photoUrl} alt="lecturerAvatar" width="30px" />
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: lecturer.avatar ?? '' }} />
                  )}
                  <span style={{ fontSize: '14px' }} className="mx-2">
                    {lecturer.fullName}
                  </span>
                </div>
              ))
            : group.lecturers.map((lecturer) => (
                <div key={lecturer.id} className="my-1 flex items-center">
                  <span style={{ fontSize: '14px' }} className="mx-2">
                    {lecturer.name}
                  </span>
                </div>
              ))}
        </div>
      </Table.Td>
      <Table.Td className="p-3">
        <div className="flex justify-center">
          {group.paper == null ? (
            <>
              <Tooltip id={tooltipId} variant={theme === 'dark' ? 'dark' : 'light'}>
                {t('group:group_does_not_have_paper')}
              </Tooltip>
              <div data-tooltip-id={tooltipId} className="cursor-help p-2">
                <CourseChosenIcon />
              </div>
            </>
          ) : group.paper.isActive ? (
            <>
              <Tooltip id={tooltipId} variant={theme === 'dark' ? 'dark' : 'light'}>
                {t('group:group_has_active_status')}
              </Tooltip>
              <div data-tooltip-id={tooltipId} className="cursor-help p-2">
                <CourseCompletedIcon />
              </div>
            </>
          ) : (
            <>
              <Tooltip id={tooltipId} variant={theme === 'dark' ? 'dark' : 'light'}>
                {t('group:group_has_passive_status')}
              </Tooltip>
              <div data-tooltip-id={tooltipId} className="cursor-help p-2">
                <CourseFailedIcon />
              </div>
            </>
          )}
        </div>
      </Table.Td>
    </Table.Tr>
  )
}
